import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi, Player, Wallet } from '../services/api';

// Rol tipleri - Backend ile uyumlu
export type UserRole = 'player' | 'vip' | 'admin' | 'user' | 'live_support' | 'risk' | 'game_management' | 'promo_management' | 'marketing';

// Rol isimleri (geriye uyumluluk için)
export const ROLE_NAMES: Record<string, string> = {
  admin: 'Admin',
  user: 'Kullanıcı',
  player: 'Oyuncu',
  vip: 'VIP',
  live_support: 'Canlı Destek',
  risk: 'Risk',
  game_management: 'Game Management',
  promo_management: 'Promo Management',
  marketing: 'Marketing'
};

// Admin panel erişim türleri (geriye uyumluluk için)
export type AdminTabType = 'users' | 'chat' | 'support' | 'market' | 'banners' | 'promo' | 'tournaments' | 'games' | 'marketing' | 'risk' | 'sponsors' | 'bonusCodes' | 'messages' | 'applications' | 'pages' | 'backup';

// Her rolün erişebildiği tablar (geriye uyumluluk için - frontend'de admin panel yok)
export const ROLE_PERMISSIONS: Record<string, AdminTabType[]> = {
  admin: [],
  user: [],
  player: [],
  vip: [],
  live_support: [],
  risk: [],
  game_management: [],
  promo_management: [],
  marketing: []
};

// Rol erişim kontrolü (geriye uyumluluk için - her zaman false döner)
export const hasAccess = (_role: UserRole, _tab: AdminTabType): boolean => {
  return false; // Frontend'de admin panel erişimi yok
};

// Kullanıcının admin paneline erişip erişemeyeceği (geriye uyumluluk için)
export const canAccessAdminPanel = (_role: UserRole): boolean => {
  return false; // Frontend'de admin panel yok
};

// Cihaz bilgisi tipi
export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string;
  isMobile: boolean;
}

// Kullanıcı verisi - SarLab Player modeli
export interface UserData {
  id: string;
  uid: string; // Geriye uyumluluk için (id ile aynı)
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  status: 'active' | 'suspended' | 'banned';
  kycStatus: 'none' | 'pending' | 'verified' | 'rejected';
  vipLevel: number;
  balance: number;
  currency: string;
  role?: UserRole; // Geriye uyumluluk için
  isActive?: boolean; // Geriye uyumluluk için
}

// Cihaz bilgisini algılayan yardımcı fonksiyon
export const getDeviceInfo = (): DeviceInfo => {
  const ua = navigator.userAgent;

  // Tarayıcı algılama
  let browser = 'Unknown';
  let browserVersion = '';

  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browser = 'Opera';
    browserVersion = ua.match(/(?:Opera|OPR)\/(\d+\.\d+)/)?.[1] || '';
  }

  // İşletim sistemi algılama
  let os = 'Unknown';
  let osVersion = '';

  if (ua.includes('Windows NT')) {
    os = 'Windows';
    const ntVersion = ua.match(/Windows NT (\d+\.\d+)/)?.[1];
    if (ntVersion === '10.0') osVersion = '10/11';
    else if (ntVersion === '6.3') osVersion = '8.1';
    else if (ntVersion === '6.2') osVersion = '8';
    else if (ntVersion === '6.1') osVersion = '7';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
    osVersion = ua.match(/Android (\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    osVersion = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || '';
  }

  // Cihaz türü algılama
  let device = 'Desktop';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  if (ua.includes('iPad')) {
    device = 'iPad';
  } else if (ua.includes('iPhone')) {
    device = 'iPhone';
  } else if (ua.includes('Android')) {
    if (ua.includes('Mobile')) {
      device = 'Android Phone';
    } else {
      device = 'Android Tablet';
    }
  } else if (isMobile) {
    device = 'Mobile';
  }

  return {
    browser,
    browserVersion,
    os,
    osVersion,
    device,
    isMobile
  };
};

interface AuthContextType {
  currentUser: UserData | null;
  userData: UserData | null;
  wallet: Wallet | null;
  loading: boolean;
  register: (data: {
    email: string;
    password: string;
    username: string;
    firstName: string;
    lastName: string;
    tcKimlikNo: string;
    phone: string;
    dateOfBirth: string;
    referralCode?: string;
  }) => Promise<void>;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);

  // Player'ı UserData'ya dönüştür
  const playerToUserData = (player: Player, walletData?: Wallet): UserData => ({
    id: player.id,
    uid: player.id, // Geriye uyumluluk için
    email: player.email,
    username: player.username,
    firstName: player.firstName,
    lastName: player.lastName,
    phone: player.phone,
    dateOfBirth: player.dateOfBirth,
    status: player.status,
    kycStatus: player.kycStatus,
    vipLevel: player.vipLevel,
    balance: walletData?.balance || 0,
    currency: walletData?.currency || 'TRY',
    role: 'player', // Geriye uyumluluk için
    isActive: player.status === 'active', // Geriye uyumluluk için
  });

  // Kullanıcı kaydı
  const register = async (data: {
    email: string;
    password: string;
    username: string;
    firstName: string;
    lastName: string;
    tcKimlikNo: string;
    phone: string;
    dateOfBirth: string;
    referralCode?: string;
  }) => {
    const result = await authApi.register({
      email: data.email,
      username: data.username,
      password: data.password,
      confirmPassword: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      tcKimlikNo: data.tcKimlikNo,
      phone: data.phone,
      dateOfBirth: data.dateOfBirth,
      referralCode: data.referralCode,
    });

    if (!result.success) {
      throw new Error(result.error || 'Kayıt işlemi başarısız');
    }

    if (result.data?.player) {
      const userData = playerToUserData(result.data.player, result.data.wallet);
      setCurrentUser(userData);
      setWallet(result.data.wallet || null);
    }
  };

  // Kullanıcı girişi
  const login = async (emailOrUsername: string, password: string) => {
    const result = await authApi.login({ emailOrUsername, password });

    if (!result.success) {
      let message = result.error || 'Giriş yapılırken bir hata oluştu';

      if (result.code === 'INVALID_CREDENTIALS') {
        message = 'E-posta/kullanıcı adı veya şifre hatalı';
      } else if (result.code === 'ACCOUNT_SUSPENDED') {
        message = 'Hesabınız askıya alınmış';
      } else if (result.code === 'ACCOUNT_BANNED') {
        message = 'Hesabınız engellenmiş';
      }

      throw new Error(message);
    }

    if (result.data?.player) {
      const userData = playerToUserData(result.data.player, result.data.wallet);
      setCurrentUser(userData);
      setWallet(result.data.wallet || null);
    }
  };

  // Kullanıcı çıkışı
  const logout = async () => {
    await authApi.logout();
    setCurrentUser(null);
    setWallet(null);
  };

  // Kullanıcı verilerini yenile
  const refreshUserData = async () => {
    const result = await authApi.me();

    if (result.success && result.data?.player) {
      const userData = playerToUserData(result.data.player, result.data.wallet);
      setCurrentUser(userData);
      setWallet(result.data.wallet || null);
    }
  };

  // Uygulama başladığında mevcut oturumu kontrol et
  useEffect(() => {
    const checkSession = async () => {
      try {
        const result = await authApi.me();

        if (result.success && result.data?.player) {
          const userData = playerToUserData(result.data.player, result.data.wallet);
          setCurrentUser(userData);
          setWallet(result.data.wallet || null);
        }
      } catch (error) {
        console.error('Session check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const value = {
    currentUser,
    userData: currentUser, // Geriye uyumluluk için
    wallet,
    loading,
    register,
    login,
    logout,
    refreshUserData,
    isAuthenticated: !!currentUser,
    isAdmin: false, // Frontend'de admin yok
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
